import { JSONContent } from '@tiptap/react';
import { StoryCardPostContentBlockFields } from '../../../services';
import {
  AudioBlock,
  AudioBlockData,
  BlockTypes,
  CalloutBlock,
  ChecklistBlock,
  ChecklistBlockData,
  CodeBlock,
  CodeBlockData,
  DelimiterBlock,
  DelimiterBlockData,
  EmbedLinkBlock,
  EmbedLinkBlockData,
  FileBlock,
  FileBlockData,
  HeaderBlock,
  HeaderBlockData,
  ImageBlock,
  ImageBlockData,
  ListBlock,
  ListBlockData,
  ListBlockElement,
  ListBlockStyle,
  ParagraphBlock,
  PdfBlock,
  PdfBlockData,
  QuoteBlock,
  QuoteBlockData,
  StoryBlock,
  StoryBlockData,
  StoryCardBlock,
  StoryCardBlockData,
  TableBlock,
  TableBlockData,
  UserBlock,
  UserBlockData,
  VideoBlock,
  VideoBlockData,
} from '../../models';

interface NodeToBlockArgs {
  node: JSONContent;
  index: number;
}

// const textWithMarks = (content: JSONContent[]) => {
//   return content
//     .map(({ text, marks }) => {
//       if (!text) {
//         return '';
//       }

//       let res = text;

//       if (marks) {
//         marks.forEach(({ type, attrs }) => {
//           switch (type) {
//             case 'bold':
//               res = `<b>${res}</b>`;
//               break;
//             case 'italic':
//               res = `<i>${res}</i>`;
//               break;
//             case 'underline':
//               res = `<u>${res}</u>`;
//               break;
//             case 'code':
//               res = `<code>${res}</code>`;
//               break;
//             case 'highlight':
//               res = `<mark>${res}</mark>`;
//               break;
//             case 'subscript':
//               res = `<sub>${res}</sub>`;
//               break;
//             case 'superscript':
//               res = `<sup>${res}</sup>`;
//               break;
//             case 'link':
//               res = `<a href="${attrs?.href}" target="_blank" rel="noopener noreferrer">${res}</a>`;
//               break;
//           }
//         });
//       }

//       return res;
//     })
//     .join('');
// };

const textWithMarksAndBreaks = (content: JSONContent[]): string => {
  return content
    .map(({ type, text, marks, content }) => {
      switch (type) {
        case 'text': {
          let res = text || '';

          if (marks) {
            marks.forEach(({ type, attrs }) => {
              switch (type) {
                case 'bold':
                  res = `<b>${res}</b>`;
                  break;
                case 'italic':
                  res = `<i>${res}</i>`;
                  break;
                case 'underline':
                  res = `<u>${res}</u>`;
                  break;
                case 'code':
                  res = `<code>${res}</code>`;
                  break;
                case 'highlight':
                  res = `<mark>${res}</mark>`;
                  break;
                case 'subscript':
                  res = `<sub>${res}</sub>`;
                  break;
                case 'superscript':
                  res = `<sup>${res}</sup>`;
                  break;
                case 'link':
                  res = `<a href="${attrs?.href}" target="_blank" rel="noopener noreferrer">${res}</a>`;
                  break;
              }
            });
          }
          return res;
        }
        case 'hardBreak':
          return '<br/>';
        default:
          // recursively handle nested content if available
          return content ? textWithMarksAndBreaks(content) : '';
      }
    })
    .join('');
};

const paragraphToBlock = ({ node, index }: NodeToBlockArgs): ParagraphBlock => {
  const { content = [] } = { ...node };

  return {
    id: `block-${index + 1}`,
    type: BlockTypes.paragraph,
    data: { text: textWithMarksAndBreaks(content) },
  };
};

const headingToBlock = ({ node, index }: NodeToBlockArgs): HeaderBlock => {
  const { content = [], attrs = {} } = { ...node };

  return {
    id: `block-${index + 1}`,
    type: BlockTypes.header,
    data: { ...attrs, text: textWithMarksAndBreaks(content) } as HeaderBlockData,
  };
};

const calloutToBlock = ({ node, index }: NodeToBlockArgs): CalloutBlock => {
  const { content = [] } = { ...node };

  return {
    id: `block-${index + 1}`,
    type: BlockTypes.callout,
    data: { icon: ' ', text: textWithMarksAndBreaks(content) },
  };
};

const quoteToBlock = ({ node, index }: NodeToBlockArgs): QuoteBlock => {
  const { attrs = {} } = { ...node };

  return {
    id: `block-${index + 1}`,
    type: BlockTypes.quote,
    data: { ...attrs } as QuoteBlockData,
  };
};

const codeToBlock = ({ node, index }: NodeToBlockArgs): CodeBlock => {
  const { attrs = {} } = { ...node };

  return {
    id: `block-${index + 1}`,
    type: BlockTypes.code,
    data: { ...attrs } as CodeBlockData,
  };
};

const delimiterToBlock = ({ node, index }: NodeToBlockArgs): DelimiterBlock => {
  const { attrs = {} } = { ...node };

  return {
    id: `block-${index + 1}`,
    type: BlockTypes.delimiter,
    data: { ...attrs } as DelimiterBlockData,
  };
};

const embedLinkToBlock = ({ node, index }: NodeToBlockArgs): EmbedLinkBlock => {
  const { attrs = {} } = { ...node };

  return {
    id: `block-${index + 1}`,
    type: BlockTypes.embedlink,
    data: { ...attrs, caption: '' } as EmbedLinkBlockData,
  };
};

const userToBlock = ({ node, index }: NodeToBlockArgs): UserBlock => {
  const { attrs = {} } = { ...node };

  return {
    id: `block-${index + 1}`,
    type: BlockTypes.user,
    data: { ...attrs } as UserBlockData,
  };
};

const storyToBlock = ({ node, index }: NodeToBlockArgs): StoryBlock => {
  const { attrs = {} } = { ...node };

  return {
    id: `block-${index + 1}`,
    type: BlockTypes.story,
    data: { ...attrs } as StoryBlockData,
  };
};

const storyCardToBlock = ({ node, index }: NodeToBlockArgs): StoryCardBlock => {
  const { attrs = {} } = { ...node };

  return {
    id: `block-${index + 1}`,
    type: BlockTypes.card,
    data: { ...attrs } as StoryCardBlockData,
  };
};

const taskListToBlock = ({ node, index }: NodeToBlockArgs): ChecklistBlock => {
  const { content = [] } = { ...node };

  return {
    id: `block-${index + 1}`,
    type: BlockTypes.checklist,
    data: {
      items: content.map(({ content = [], attrs }) => {
        //No nesting
        const { content: contentInner = [] } = { ...content[0] };
        return { ...attrs, text: textWithMarksAndBreaks(contentInner) };
      }),
    } as ChecklistBlockData,
  };
};

const parseListItems = (content: JSONContent[]): ListBlockElement[] => {
  return content.map(({ content = [], attrs }) => {
    let textContent: JSONContent[] = [];
    let items: ListBlockElement[] = [];

    for (const node of content) {
      const { type = '', content = [] } = node;

      if (['bulletList', 'orderedList'].includes(type)) {
        items = parseListItems(content);
      } else if (type === 'paragraph') {
        textContent = content;
      }
    }

    return { ...attrs, content: textWithMarksAndBreaks(textContent), items: items };
  });
};

const orderedListToBlock = ({ node, index }: NodeToBlockArgs): ListBlock => {
  const { content = [] } = { ...node };

  return {
    id: `block-${index + 1}`,
    type: BlockTypes.list,
    data: { style: ListBlockStyle.ordered, items: parseListItems(content) } as ListBlockData,
  };
};

const bulletListToBlock = ({ node, index }: NodeToBlockArgs): ListBlock => {
  const { content = [] } = { ...node };

  return {
    id: `block-${index + 1}`,
    type: BlockTypes.list,
    data: { style: ListBlockStyle.unordered, items: parseListItems(content) } as ListBlockData,
  };
};

const imageToBlock = ({ node, index }: NodeToBlockArgs): ImageBlock => {
  const { attrs = {} } = { ...node };

  return {
    id: `block-${index + 1}`,
    type: BlockTypes.image,
    data: { ...attrs } as ImageBlockData,
  };
};

const audioToBlock = ({ node, index }: NodeToBlockArgs): AudioBlock => {
  const { attrs = {} } = { ...node };

  return {
    id: `block-${index + 1}`,
    type: BlockTypes.audio,
    data: { ...attrs } as AudioBlockData,
  };
};

const videoToBlock = ({ node, index }: NodeToBlockArgs): VideoBlock => {
  const { attrs = {} } = { ...node };

  return {
    id: `block-${index + 1}`,
    type: BlockTypes.video,
    data: { ...attrs } as VideoBlockData,
  };
};

const pdfToBlock = ({ node, index }: NodeToBlockArgs): PdfBlock => {
  const { attrs = {} } = { ...node };

  return {
    id: `block-${index + 1}`,
    type: BlockTypes.pdf,
    data: { ...attrs } as PdfBlockData,
  };
};

const fileToBlock = ({ node, index }: NodeToBlockArgs): FileBlock => {
  const { attrs = {} } = { ...node };

  return {
    id: `block-${index + 1}`,
    type: BlockTypes.file,
    data: { ...attrs } as FileBlockData,
  };
};

const cellToColumnWidth = ({ attrs }: JSONContent): number | null => {
  const [width] = attrs?.colwidth ?? [];

  return width ? Math.round(width) : null;
};

const tableToBlock = ({ node, index }: NodeToBlockArgs): TableBlock => {
  const { content = [] } = { ...node };

  const rows = [];
  const withHeadings = content[0].content?.[0].type === 'tableHeader';

  for (const row of content) {
    rows.push(
      row.content?.map((cell) =>
        (cell.content || []).map((block) => textWithMarksAndBreaks(block.content || [])).join('\n')
      ) || []
    );
  }

  return {
    id: `block-${index + 1}`,
    type: BlockTypes.table,
    data: {
      withHeadings,
      content: rows,
      columnWidths: (content[0].content ?? []).map(cellToColumnWidth),
    } as TableBlockData,
  };
};

export const contentToBlocksConverter = (
  content: JSONContent[]
): StoryCardPostContentBlockFields[] => {
  return content
    .map((node, index) => {
      const { type } = node;

      switch (type) {
        case 'paragraph':
          return paragraphToBlock({ node, index });
        case 'heading':
          return headingToBlock({ node, index });
        case 'taskList':
          return taskListToBlock({ node, index });
        case 'orderedList':
          return orderedListToBlock({ node, index });
        case 'bulletList':
          return bulletListToBlock({ node, index });
        case 'calloutNode':
          return calloutToBlock({ node, index });
        case 'quoteNode':
          return quoteToBlock({ node, index });
        case 'codeNode':
          return codeToBlock({ node, index });
        case 'delimiterNode':
          return delimiterToBlock({ node, index });
        case 'embedLinkNode':
          return embedLinkToBlock({ node, index });
        case 'userNode':
          return userToBlock({ node, index });
        case 'storyNode':
          return storyToBlock({ node, index });
        case 'storyCardNode':
          return storyCardToBlock({ node, index });
        case 'imageNode':
          return imageToBlock({ node, index });
        case 'audioNode':
          return audioToBlock({ node, index });
        case 'videoNode':
          return videoToBlock({ node, index });
        case 'pdfNode':
          return pdfToBlock({ node, index });
        case 'fileNode':
          return fileToBlock({ node, index });
        case 'table':
          return tableToBlock({ node, index });
        default:
          return undefined;
      }
    })
    .filter((block) => block !== undefined) as StoryCardPostContentBlockFields[];
};
